import { useAPIQuery } from "@dev2_techwalnut/tw-api-query-hook";
import { Table } from "@dev2_techwalnut/tw-react-crud-dashlite";
import React, { useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";

const badgeClass = (lifecycle) => {
    const value = (lifecycle || "").toLowerCase();
    if (value === "resolved") return "badge badge-dot bg-success";
    if (value === "doing") return "badge badge-dot bg-info";
    if (value === "reopen") return "badge badge-dot bg-danger";
    return "badge badge-dot bg-warning";
};

const formatTs = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
};

const OnsiteIssueList = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const machineId = location.state?.machineId;
    const machineNameFromState = location.state?.machineName;
    const [searchParams, setSearchParams] = useSearchParams();
    const initialEntries = Number(searchParams.get("entries")) || 10;
    const initialPage = Number(searchParams.get("page")) || 1;
    const [entries, setEntries] = useState(initialEntries);
    const [pageIndex, setPageIndex] = useState(initialPage - 1);
    const [globalFilterValue, setGlobalFilterValue] = useState(
        searchParams.get("q") || ""
    );
    const [lifecycle, setLifecycle] = useState(
        searchParams.get("lifecycle") || ""
    );
    const [sorting, setSorting] = useState([]);

    const { useFetch: useFetchLookups } = useAPIQuery({
        key: "api/onsite-issues/lookups",
        endpoint: "/api/onsite-issues/lookups/",
    });
    const { data: lookupResponse } = useFetchLookups({
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });
    const statusOptions = useMemo(() => {
        const nested = lookupResponse?.results?.data || lookupResponse?.data || lookupResponse;
        if (Array.isArray(nested?.statuses)) return nested.statuses;
        if (Array.isArray(lookupResponse?.statuses)) return lookupResponse.statuses;
        return [
            { name: "Pending", value: "Pending" },
            { name: "Doing", value: "Doing" },
            { name: "Resolved", value: "Resolved" },
            { name: "REOPEN", value: "REOPEN" },
        ];
    }, [lookupResponse]);

    const { useFetch: useFetchMachines } = useAPIQuery({
        key: "api/onsite-issues/machines",
        endpoint: "/api/onsite-issues/machines/",
    });
    const { useFetch } = useAPIQuery({
        key: ["api/onsite-issues", machineId],
        endpoint: "/api/onsite-issues/",
    });
    const { useDelete } = useAPIQuery({
        key: ["api/onsite-issues", machineId],
        endpoint: "/api/onsite-issues/",
    });

    const { data: machineResponse } = useFetchMachines({
        params: { entries: 500, page_no: 1 },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });

    const { data, isLoading, isFetching } = useFetch({
        params: {
            machine_id: machineId,
            page_no: pageIndex + 1,
            entries,
            q: globalFilterValue || undefined,
            lifecycle: lifecycle || undefined,
            sort_q: sorting.map((item) => (item?.desc ? "desc" : "asc")).join(",") || undefined,
            sort_c: sorting.map((item) => item?.id).join(",") || undefined,
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 0,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });

    const { mutate: deleteData } = useDelete();

    const machineName = useMemo(() => {
        const machines = machineResponse?.results?.data || machineResponse?.data || [];
        const list = Array.isArray(machines) ? machines : [];
        const match = list.find((row) => String(row.id) === String(machineId));
        return match?.machine_name || machineNameFromState || `Machine ${machineId}`;
    }, [machineResponse, machineId, machineNameFromState]);

    const columns = [
        {
            accessor: "description",
            header: "Description",
            sortable: true,
            cell: (row) => {
                const text = row.description || "";
                return text.length > 80 ? `${text.slice(0, 80)}…` : text || "—";
            },
        },
        {
            accessor: "lifecycle",
            header: "Status",
            sortable: true,
            cell: (row) => (
                <span className={badgeClass(row.lifecycle)}>
                    {row.lifecycle_label || row.lifecycle}
                </span>
            ),
        },
        {
            accessor: "created_by_name",
            header: "Reported by",
            cell: (row) => row.created_by_name || "—",
        },
        {
            accessor: "assigned_to_name",
            header: "Assigned to",
            sortable: true,
            cell: (row) => row.assigned_to_name || "—",
        },
        {
            accessor: "tags",
            header: "Tags",
            cell: (row) =>
                Array.isArray(row.tags) && row.tags.length
                    ? row.tags.map((tag) => tag.name || tag.value).join(", ")
                    : "—",
        },
        {
            accessor: "created_at",
            header: "Created",
            sortable: true,
            cell: (row) => formatTs(row.created_at),
        },
    ];

    const rows = Array.isArray(data?.results?.data)
        ? data.results.data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];

    const handleGlobalFilterChange = (value) => {
        const q = value?.trim() || "";
        setGlobalFilterValue(q);
        setSearchParams((params) => {
            if (q) params.set("q", q);
            else params.delete("q");
            return params;
        });
    };

    const handleLifecycleChange = (value) => {
        setLifecycle(value);
        setPageIndex(0);
        setSearchParams((params) => {
            if (value) params.set("lifecycle", value);
            else params.delete("lifecycle");
            params.set("page", "1");
            return params;
        });
    };

    const handlePagination = (value) => {
        setSearchParams((params) => {
            params.set("entries", value.pageSize);
            params.set("page", value.pageIndex + 1);
            return params;
        });
        setPageIndex(value.pageIndex);
        setEntries(value.pageSize);
    };

    const handleDeleteRow = (row) => {
        Swal.fire({
            text: "Are you sure you want to delete this issue?",
            showDenyButton: true,
            confirmButtonText: "Yes",
            denyButtonText: "No, cancel!",
        }).then((result) => {
            if (result.isConfirmed) {
                deleteData({ id: row.original.id });
            }
        });
    };

    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content">
                        <h3 className="nk-block-title page-title">{machineName}</h3>
                        <p className="text-soft">
                            On-site issues for this machine. Pending issues are shown first.
                        </p>
                    </div>
                    <div className="nk-block-head-content d-flex" style={{ gap: 12, alignItems: "end" }}>
                        <div className="form-group">
                            <label className="form-label">Status</label>
                            <select
                                className="form-control"
                                value={lifecycle}
                                onChange={(event) => handleLifecycleChange(event.target.value)}
                            >
                                <option value="">All</option>
                                {statusOptions.map((row) => (
                                    <option key={row.value} value={row.value}>
                                        {row.name || row.value}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <NavLink to="/onsite-issues" className="btn btn-outline-light">
                            Back
                        </NavLink>
                        <NavLink
                            to="/create-onsite-issue"
                            state={{ machineId, machineName }}
                            className="btn btn-primary"
                        >
                            <em className="icon ni ni-plus"></em>
                            <span>Add issue</span>
                        </NavLink>
                    </div>
                </div>
            </div>
            <Table
                data={rows}
                columns={columns}
                pagination
                pageIndex={pageIndex}
                pageSize={entries}
                rowCount={typeof data?.count === "number" ? data.count : rows.length}
                globalSearch
                isLoading={isLoading || isFetching}
                editRows
                deleteRows
                filterModal={false}
                selectRows={false}
                defaultGlobalSearchValue={globalFilterValue || undefined}
                onGlobalFilterChange={handleGlobalFilterChange}
                onPaginationChange={handlePagination}
                onSortingChange={setSorting}
                onRowEdit={(row) =>
                    navigate("/edit-onsite-issue", {
                        state: {
                            machineId,
                            machineName,
                            issueId: row.original.id,
                            issue: row.original,
                        },
                    })
                }
                onRowDelete={handleDeleteRow}
            />
        </>
    );
};

export default OnsiteIssueList;
